import { canHideWhenEmpty } from "../state/attributes";
import type { DocumentDesignItem } from "../state/types";

export interface RecordValues {
  data: Record<string, string>;
  loading: boolean;
}

export interface AnchorBox {
  id: string;
  top: number;
  height: number;
}

export interface PlacedElement {
  top: number;
  /** The top this element would have with nothing hidden. */
  layoutTop: number;
  movedByHiding: boolean;
}

export interface HiddenWhenEmptyLayout {
  isHidden: (itemId: string) => boolean;
  /** Must be called in render order, so an anchor is always placed first. */
  place: (item: DocumentDesignItem, anchor?: AnchorBox) => PlacedElement;
}

export function planHiddenElements(
  items: DocumentDesignItem[],
  values: RecordValues,
): HiddenWhenEmptyLayout {
  const hiddenIds = collectHiddenIds(items, values);
  const onlyFollowerIds = collectOnlyFollowerIds(items);
  const layoutTops = new Map<string, number>();
  const movedIds = new Set<string>();

  return {
    isHidden: (itemId) => hiddenIds.has(itemId),

    place(item, anchor) {
      const distance = item.anchorDistance ?? 0;
      let top = item.top ?? 0;
      let layoutTop = top;

      if (anchor) {
        const anchorIsHidden = hiddenIds.has(anchor.id);
        // A hidden anchor also swallows the distance to its only follower.
        // Several followers keep their own distance, otherwise they would land
        // on top of each other.
        const swallowsDistance = anchorIsHidden && onlyFollowerIds.has(item.id);

        top = swallowsDistance
          ? anchor.top
          : anchor.top + (anchorIsHidden ? 0 : anchor.height) + distance;

        layoutTop =
          (layoutTops.get(anchor.id) ?? anchor.top) + anchor.height + distance;

        if (anchorIsHidden || movedIds.has(anchor.id)) {
          movedIds.add(item.id);
        }
      }

      layoutTops.set(item.id, layoutTop);
      if (hiddenIds.has(item.id)) {
        movedIds.add(item.id);
      }

      return { top, layoutTop, movedByHiding: movedIds.has(item.id) };
    },
  };
}

function collectHiddenIds(
  items: DocumentDesignItem[],
  values: RecordValues,
): Set<string> {
  // While the record is loading every value looks empty, and hiding on that
  // would collapse the document and write the collapsed tops back into state.
  if (values.loading) {
    return new Set();
  }

  // The rendered text already resolves formulas, selects and linked records, so
  // an empty string is the one signal that covers every field type.
  const hidden = items.filter(
    (item) =>
      item.hideWhenEmpty &&
      canHideWhenEmpty(item) &&
      !values.data[item.field_slug],
  );

  return new Set(hidden.map((item) => item.id));
}

function collectOnlyFollowerIds(items: DocumentDesignItem[]): Set<string> {
  const followersByAnchor = new Map<string, string[]>();

  for (const item of items) {
    if (item.position !== "anchored" || !item.anchorId) {
      continue;
    }
    const followers = followersByAnchor.get(item.anchorId) ?? [];
    followers.push(item.id);
    followersByAnchor.set(item.anchorId, followers);
  }

  const onlyFollowers = [...followersByAnchor.values()]
    .filter((followers) => followers.length === 1)
    .map(([followerId]) => followerId);

  return new Set(onlyFollowers);
}
